import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { DirectDealBasket } from '@services/marketBasketReports.types'

type Props = {
	basket: DirectDealBasket
	isSelected: boolean
	onSelect: (basket: DirectDealBasket) => void
}

export default function BasketCard({ basket, isSelected, onSelect }: Props) {
	return (
		<Paper
			variant="outlined"
			sx={{
				px: 3,
				py: 2,
				borderRadius: 2,
				cursor: 'pointer',
				borderColor: isSelected ? 'primary.main' : 'divider',
				bgcolor: isSelected ? 'action.selected' : 'background.paper',
				'&:hover': { borderColor: 'primary.main' },
			}}
			onClick={() => onSelect(basket)}
		>
			<Stack spacing={1}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between">
					<Box>
						<Typography variant="body1" fontWeight={600}>
							{basket.displayName}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{basket.salesforceCaseId ?? 'N/A'}
						</Typography>
					</Box>
					<Button
						variant={isSelected ? 'contained' : 'outlined'}
						sx={{ p: 1 }}
						size="small"
						onClick={(e) => {
							e.stopPropagation()
							onSelect(basket)
						}}
						data-testid={`basket-card-select-${basket.marketBasketId}`}
					>
						{isSelected ? 'Selected' : 'Select'}
					</Button>
				</Stack>

				<Stack direction="row" justifyContent="space-between" gap={1}>
					<CategoryStat
						label="Recommend"
						count={basket.recommendedAccepted}
						total={basket.recommendedTotal}
						color="success.main"
					/>
					<CategoryStat
						label="Decline"
						count={basket.declineRejected}
						total={basket.declineTotal}
						color="error.main"
					/>
				</Stack>
			</Stack>
		</Paper>
	)
}

type CategoryStatProps = {
	label: string
	count: number
	total: number
	color: string
}

function CategoryStat({ label, count, total, color }: CategoryStatProps) {
	const pct = total > 0 ? Math.round((count / total) * 100) : 0
	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-around',
			}}
			data-testid={`category-stat-${label.toLowerCase()}`}
		>
			<Typography variant="body2" fontWeight={600} color="text.secondary" noWrap>
				{label}
			</Typography>
			<Typography
				variant="h6"
				color={color}
				noWrap
				data-testid={`category-stat-count-${label.toLowerCase()}`}
			>
				{count}/{total}
				{total > 0 ? ` (${pct}%)` : ' (100%)'}
			</Typography>
		</Box>
	)
}
